import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";

import { withRouter, Link } from "react-router-dom";
import queryString from "query-string";
import styled from "styled-components";
import moment from "moment";
import _ from "lodash";

import { Grid } from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import LinearProgress from "@material-ui/core/LinearProgress";

import { getLoansForComparison, downloadLoansForComparison, getLoanModificationHistory, downloadLoanModificationHistory } from "../../../store/state/loan/loan";
import {getConfigItemByParentId} from 'store/state/configItems';
import { getDealName } from "../../../store/state/deal";

import { ToastError } from "../../Global/Toasts/Toasty";
import BreadCrumbGrid from "../../Global/BreadCrumb/BreadCrumbGrid";
import BackButton from "../../Global/Buttons/BackButton";
import SecondaryButton from "../../Global/Buttons/SecondaryButton";

import { SISENSE_REPORT_DASHBOARDS } from "constants/configItemParents";

import LoanAmount from "./components/LoanAmount";
import Term from "./components/Term";
import RateType from "./components/RateType";
import InterestOnlyPeriod from "./components/InterestOnlyPeriod";
import LenderFee from "./components/LenderFee";
import Prepayment from "./components/Prepayment";
import Covenants from "./components/Covenants";
import Recourse from "./components/Recourse";
import Reserves from "./components/Reserves";
import HedgeRequirement from "./components/HedgeRequirement";
import AnalysisHeader from "./components/Analysis/AnalysisHeader";
import QuoteAnalysis from "./components/Analysis/QuoteAnalysis";
import Other from "./components/Other";
import Notes from "./components/Notes";

const PageWrapper = styled.div`
  width: 1200px;
  padding: 16px;
`;
const ButtonBarContainer = styled.div`
  display: flex;
  flex-flow: row;
`;
const StyledLabel = styled.div`
  margin: 10px;
  font-size: 24px !important;
  color: #606061;
  font-weight: bold;
`;
const LenderHeader = styled(Grid)`
  padding: 0 56px 4px 24px;
  && {
    border-bottom: 1px solid grey;
  }
`;
const LenderName = styled.div`
  font-size: 17px;
  white-space: pre-wrap;
  word-break: break-word;
  font-weight: bold;
  text-align: center;
`;
const StyledSecondaryButton = styled(SecondaryButton)`
  display: inline-block;
`;
const Spacer = styled.div`
  flex-grow: 1;
`;
const StyledLinearProgress = styled(LinearProgress)`
  margin-top: 2%;
`;

class QuoteComparison extends Component {
  constructor(props) {
    super(props);
    this.timer = null;

    this.state = {
      loanIds: [],
      combined: false,
      isAnalysisOpen: false,
      completed: 1
    };
  }

  componentDidMount() {
    const { location, match, loanModificationHistory } = this.props;

    this.props.getConfigItemByParentId({
      id: SISENSE_REPORT_DASHBOARDS
    });
    const params = queryString.parse(location.search, { arrayFormat: "bracket" });
    this.props.getDealName({ dealId: match.params.dealId, subKey: match.params.dealId });

    const isModificationHistory = params.isModificationHistory === "true";
    const primaryLoanId = params.primaryLoanId && parseInt(params.primaryLoanId);
    const dealId = match.params.dealId && parseInt(match.params.dealId);

    this.setState({
      loanIds: params.loanId,
      combined: params.methodName === "combine",
      isModificationHistory,
      primaryLoanId,
      dealId
    });

    if (!isModificationHistory) {
      this.timer = setInterval(this.progress, 150 * params.loanId.length);
      this.calculate(null, true, false);
    } else if (!loanModificationHistory || !loanModificationHistory[primaryLoanId]) {
      this.props.getLoanModificationHistory(dealId, primaryLoanId);
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (state.isModificationHistory && props.loanModificationHistory && state.primaryLoanId && props.loanModificationHistory[state.primaryLoanId]) {
      state.loans = props.loanModificationHistory[state.primaryLoanId];
      state.loanDetails = state.loans;
    } else if (!state.isModificationHistory) {
      state.loans = props.loans;
      state.loanDetails = props.quoteMetricAnalysis;
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.state.isModificationHistory) {
      const { quoteMetricAnalysis } = this.props;
      const prevQuoteMetricAnalysis = prevProps.quoteMetricAnalysis;
      const loansHaveNoLoanTermMonths = _.get(quoteMetricAnalysis[0], "loansHaveNoLoanTermMonths");
      if (
        quoteMetricAnalysis !== null &&
        prevQuoteMetricAnalysis !== null &&
        quoteMetricAnalysis !== prevQuoteMetricAnalysis &&
        loansHaveNoLoanTermMonths === true
      ) {
        ToastError("No maturity dates set for selected quotes.");
      }
    }
  }

  componentWillUnmount() {
    if (!this.state.isModificationHistory) {
      clearInterval(this.timer);
    }
  }

  showAnalysis = () => {
    this.onAnalysisChange(true);
  };

  closeAnalysis = () => {
    this.setState({
      isAnalysisOpen: false
    });
  };

  onAnalysisChange = isAnalysisOpen => {
    this.setState({
      isAnalysisOpen
    });
  };

  goBack = () => {
    this.props.history.goBack();
  };

  downloadLoans = values => {
    const { primaryLoanId, loanDetails } = this.state;

    const dealId = parseInt(this.props.match.params.dealId);
    if (this.state.isModificationHistory) {
      this.props.downloadLoanModificationHistory(dealId, primaryLoanId);
    } else {
      const { location } = this.props;
      const params = queryString.parse(location.search, { arrayFormat: "bracket" });
      const payload = this.props.quoteMetricAnalysis;
      this.props.downloadLoansForComparison(payload);
    }
  };

  calculate = (values, showProgress, recalculate) => {
    const { location } = this.props;
    const params = queryString.parse(location.search, { arrayFormat: "bracket" });
    const dealId = parseInt(this.props.match.params.dealId);

    const payload = {
      name: `Quote Metric Analysis for loans ${params.loanId}`,
      exitDate: !values ? null : moment(values.exitDate).format("YYYY-MM-DD"),
      loanIds: params.loanId,
      marketEnvironmentId: !values ? 1 : values.marketEnvironment,
      parallelShiftAmount: !values ? null : values.parallelShiftAmount,
      standardDeviationAmount: !values ? null : values.standardDeviationAmount,
      shouldCombine: params.methodName === "combine",
      methodName: params.methodName,
      subKey: queryString,
      dealId,
      showProgress,
      useAggregateExisting: params.useAggregateExisting === "true",
      recalculate
    };

    this.props.getLoansForComparison(payload);
  };

  progress = () => {
    const { quoteMetricAnalysis } = this.props;
    const { completed } = this.state;
    if (completed !== 0) {
      if (quoteMetricAnalysis.length > 0) {
        this.setState({ completed: 0 });
      } else {
        const diff = Math.random() * 10;
        this.setState({ completed: Math.min(completed + diff, 100) });
      }
    } else {
      clearInterval(this.timer);
    }
  };


  getYearOverYearDSCR(loanDetails) {
    let yoyArrayTemp = [];
    loanDetails.forEach(x => { yoyArrayTemp =  (x.yearOverYearDSCR && x.yearOverYearDSCR.length > yoyArrayTemp.length) ? x.yearOverYearDSCR : yoyArrayTemp; });
    return yoyArrayTemp.map(x => {
                                  const values = this.findYoYDSCRValues(x.year); 
                                  return { yoyLabel: x.year, values, isExistingFinancing: false, originalLoan: false };
                                });
  }
  findYoYDSCRValues = (year) => {
                                  const { loanDetails } = this.state;
                                  let values = [];
                                  loanDetails.forEach( (x, index) => {
                                    let loanYearOverYear = {
                                      id: `${x.loanId}-${index}`,
                                      isBusted: false,
                                      loanId: x.loanId,
                                      type: "DSCR",
                                      year,
                                      value: null,
                                    }
                                    if(x.yearOverYearDSCR) {
                                      const found = x.yearOverYearDSCR.filter( y => y.year == year);
                                      if(found[0]){
                                        loanYearOverYear = found[0];
                                        loanYearOverYear.isBusted = x.covenantsDscrStabilized && x.covenantsDscrStabilized > 0 ? loanYearOverYear.value <  x.covenantsDscrStabilized : false;
                                      }
                                    }
                                    values.push(loanYearOverYear);
                                  });
                                  return values;
  }

  getYearOverYearDebtYield(loanDetails) {
    let yoyArrayTemp = [];
    loanDetails.forEach(x => { yoyArrayTemp =  (x.yearOverYearDebtYield && x.yearOverYearDebtYield.length > yoyArrayTemp.length) ? x.yearOverYearDebtYield : yoyArrayTemp; });
    return yoyArrayTemp.map(x => {
                                  const values = this.findYoYDebtYieldValues(x.year); 
                                  return { yoyLabel: x.year, values, isExistingFinancing: false, originalLoan: false };
                                });
  }
  findYoYDebtYieldValues = (year) => {
    const { loanDetails } = this.state;
    let values = [];
    loanDetails.forEach( (x, index) => {
      let loanYearOverYear = {
        id: `${x.loanId}-${index}`,
        isBusted: false,
        loanId: x.loanId,
        type: "DebtYield",
        year,
        value: null,
      }
      if(x.yearOverYearDebtYield) {
        const found = x.yearOverYearDebtYield.filter( y => y.year == year);
        if(found[0]){
          loanYearOverYear = found[0];
          loanYearOverYear.isBusted = x.covenantsDebtYieldStabilized && x.covenantsDebtYieldStabilized > 0 ? loanYearOverYear.value <  x.covenantsDebtYieldStabilized : false;
        }
      }
      values.push(loanYearOverYear);
    });
    return values;
}

  render() {
    const { combinedLoans, dealMap, authentication, quoteMetricAnalysisError, dashboardId } = this.props;
    const { isAnalysisOpen, combined, isModificationHistory, primaryLoanId, loans, dealId, loanDetails } = this.state;

    if (quoteMetricAnalysisError)
      return (
        <>
          <StyledLabel>Error Running Quote Analysis</StyledLabel>
          <StyledLabel>Please Refresh your window. Contact your administrator if the problem persists.</StyledLabel>
        </>
      );

    if (!isModificationHistory && loanDetails.length === 0)
      return (
        <>
          <StyledLabel>Running Quote Analysis</StyledLabel> <StyledLinearProgress variant="determinate" value={this.state.completed} />
        </>
      );

    if ((!combined && !loans) || (combined && !combinedLoans)) return null;

    let initialValues = null;
    if (loanDetails && loanDetails.length > 0) {
      initialValues = {
        marketEnvironment: 1,
        parallelShiftAmount: "",
        standardDeviationAmount: "",
        exitDate: moment(loanDetails[0].exitDate).toString(),
        loansHaveNoLoanTermMonths: loanDetails[0].loansHaveNoLoanTermMonths
      };
    }

    let gridCol;
    if (loanDetails.length <= 2) {
      gridCol = 4;
    } else if (loanDetails.length >= 6) {
      gridCol = 1;
    } else if (loanDetails.length >= 3) {
      gridCol = 2;
    } else {
      gridCol = 3;
    }

    let yearOverYearDSCR = this.getYearOverYearDSCR(loanDetails);
    let yearOverYearDebtYield = this.getYearOverYearDebtYield(loanDetails);

    return (
      <>
        <PageWrapper>
          {!authentication.isGuest && (
            <BreadCrumbGrid>
              {!isModificationHistory && (
                <>
                  {_.get(dealMap, `${dealId}.name`, "-")} / {combined ? "Quote Combine" : "Quote Comparison"}
                </>
              )}
              {isModificationHistory && (
                <>
                  {_.get(dealMap, `${dealId}.name`, "-")} / Modification History
                </>
              )}
            </BreadCrumbGrid>
          )}
          <ButtonBarContainer>
            <BackButton onClick={this.goBack} title={isModificationHistory ? "Back to loan details" : "Back To Quotes"} />
            <Spacer />
            {this.state.combined && (
              <StyledSecondaryButton
                onClick={() => {
                  const url = `/portfolio/funds/free/deals/${dealId}/cashflowsPage?showActiveLoans=true&loanId=${this.state.loanIds[0]}&loanId2=${this.state.loanIds[1]}&showQuotes=true`;
                  this.props.history.push(url);
                }}
              >
                View Combined Cashflows
              </StyledSecondaryButton>
            )}
            <StyledSecondaryButton onClick={this.downloadLoans}>
              <GetApp />
              Export
            </StyledSecondaryButton>
          </ButtonBarContainer>
          {isAnalysisOpen && initialValues && !isModificationHistory && <QuoteAnalysis initialValues={initialValues} calculate={this.calculate} />}
          <LenderHeader container direction="row">
            <Grid item xs={2} />
            {loanDetails.map((x,i) => (
              <Grid item xs={gridCol} key={`loanDetailsGrid${i}`}>
                <LenderName key={`loanDetailsGridLenderName${i}`}>{x.leadCompanyName}</LenderName>
              </Grid>
            ))}
          </LenderHeader>
          {!isModificationHistory && (
            <AnalysisHeader
              authentication={authentication}
              isCombined={combined}
              analysisInfo={loanDetails}
              isAnalysisOpen={isAnalysisOpen}
              showAnalysis={this.showAnalysis}
              closeAnalysis={this.closeAnalysis}
              yearOverYearDSCR={yearOverYearDSCR}
              yearOverYearDebtYield={yearOverYearDebtYield}
              dashboardId={dashboardId}
            />
          )}
          <LoanAmount isCombined={combined} loans={loanDetails} />
          <Term isCombined={combined} loans={loanDetails} />
          <RateType isCombined={combined} loans={loanDetails} />
          <InterestOnlyPeriod isCombined={combined} loans={loanDetails} />
          <LenderFee isCombined={combined} loans={loanDetails} />
          <Prepayment isCombined={combined} loans={loanDetails} />
          <Covenants isCombined={combined} loans={loanDetails} />
          <Recourse isCombined={combined} loans={loanDetails} />
          <Reserves isCombined={combined} loans={loanDetails} />
          <HedgeRequirement isCombined={combined} loans={loanDetails} />
          {!isModificationHistory && (<Other isCombined={combined} loans={loanDetails} />)}
          <Notes isCombined={combined} loans={loanDetails} />
        </PageWrapper>
      </>
    );
  }
}

const mapStateToProps = ({ loan, deal, authentication, configItems }) => ({
  loans: loan.loansForComparison,
  loanModificationHistory: loan.loanModificationHistory,
  dealMap: deal.dealNames,
  combinedLoans: loan.combinedLoansForComparison,
  quoteMetricAnalysis: loan.quoteMetricAnalysis !== "ERROR" ? _.sortBy(loan.quoteMetricAnalysis, ['sortOrder', 'loanId']) : [],
  quoteMetricAnalysisError: loan.quoteMetricAnalysis === "ERROR",
  authentication,
  dashboardId: configItems && 
    configItems.configItemByParent["7a382d0d-8597-11ea-ad2a-00ff2e1361dc"] && 
    configItems.configItemByParent["7a382d0d-8597-11ea-ad2a-00ff2e1361dc"].filter(x=>x.id=='7a38339f-8597-11ea-ad2a-00ff2e1361dc').length > 0 ? 
      configItems.configItemByParent["7a382d0d-8597-11ea-ad2a-00ff2e1361dc"].filter(x=>x.id=='7a38339f-8597-11ea-ad2a-00ff2e1361dc')[0].value : ""
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    getLoansForComparison,
    getDealName,
    downloadLoansForComparison,
    getLoanModificationHistory,
    downloadLoanModificationHistory,
    getConfigItemByParentId
  })
)(QuoteComparison);
